import { useState } from 'react';
import PageNavbar from '../../../components/feature/PageNavbar';
import Footer from '../../home/components/Footer';
import OnlineReferralModal from '../../home/components/OnlineReferralModal';

export default function StartProgramPage() {
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <PageNavbar />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-teal-50 via-white to-emerald-50 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl">
            <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              Intensive Neuroplasticity Treatment Program
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
              START – Short Term Acute Rehabilitation for Trauma
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed mb-4 italic">
              Reset the Brain. Restore Function. Reclaim Life.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mb-8">
              Intensive Neuroplasticity Treatment Program
            </p>
            <p className="text-lg text-gray-600 leading-relaxed max-w-3xl">
              START is a 2–4 week intensive neuroplasticity-focused treatment program for
              individuals experiencing trauma, PTSD and known or suspected Traumatic
              Brain Injury (TBI)
            </p>
            <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mt-4">
              It integrates advanced diagnostics with evidence-based medical, psychiatric
              and rehabilitation therapies to address both TBI and trauma-related
              conditions.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">

        {/* Who It's For */}
        <section className="mb-24">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Who It&rsquo;s For</h2>
          <ul className="space-y-3 max-w-3xl">
            <li className="text-lg text-gray-600 leading-relaxed">
              Individuals with PTSD and suspected or confirmed TBI
            </li>
            <li className="text-lg text-gray-600 leading-relaxed">
              Patients with trauma-related cognitive, neurological or functional impairment
            </li>
            <li className="text-lg text-gray-600 leading-relaxed">
              Individuals requiring coordinated medical and rehabilitation assessment
            </li>
            <li className="text-lg text-gray-600 leading-relaxed">
              Partners and family members (where appropriate)
            </li>
          </ul>
        </section>

        {/* What the Program Addresses */}
        <section className="mb-24">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What the Program Addresses</h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-6 max-w-3xl">
            START focuses on:
          </p>
          <div className="max-w-4xl columns-2 md:columns-3 gap-8">
            {[
              'PTSD and trauma recovery',
              'TBI diagnosis and sequelae',
              'Cognitive impairment',
              'Sleep disturbance',
              'Chronic pain',
              'Emotional dysregulation',
              'Functional mobility decline',
              'Medication optimisation',
              'Secondary seizure disorders from TBI',
            ].map((item, index) => (
              <p key={index} className="text-gray-700 py-1.5 break-inside-avoid">
                {item}
              </p>
            ))}
          </div>
        </section>

        {/* Comprehensive Assessment Includes */}
        <section className="mb-24">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Comprehensive Assessment Includes</h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-6 max-w-3xl">
            Where appropriate:
          </p>
          <div className="max-w-4xl columns-2 md:columns-3 gap-8">
            {[
              'Neuropsychiatric assessment',
              'Rehabilitation assessment',
              'MRI and neuroimaging',
              'EEG and long-term video EEG',
              'Neuropsychological testing',
              'Pathology and genetic testing',
              'Clinical impairment and disability ratings',
              'Polysonography sleep testing',
            ].map((item, index) => (
              <p key={index} className="text-gray-700 py-1.5 break-inside-avoid">
                {item}
              </p>
            ))}
          </div>
        </section>

        {/* Therapeutic Interventions May Include */}
        <section className="mb-24">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Therapeutic Interventions May Include</h2>
          <div className="max-w-4xl columns-2 md:columns-3 gap-8 mb-6">
            {[
              'Trauma-focused CBT',
              'EMDR',
              'RESTORE exposure therapy',
              'rTMS',
              'Cognitive rehabilitation',
              'AI-driven neuroplasticity rehab',
              'Robotics and VR rehabilitation',
              'Functional electrical stimulation',
              'Physiotherapy and OT',
              'Hydrotherapy and exoskeleton therapy',
              'Biofeedback',
              'Acupuncture, Tai Chi, Yoga',
              'Laughter Clinics',
              'All-abilities sport and recreation',
            ].map((item, index) => (
              <p key={index} className="text-gray-700 py-1.5 break-inside-avoid">
                {item}
              </p>
            ))}
          </div>
          <p className="text-lg text-gray-600 leading-relaxed max-w-3xl">
            Optional third week focuses on recreation and reintegration activities.
          </p>
        </section>

        {/* How to Access */}
        <section className="mb-24">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">How to Access</h2>
          <div className="max-w-3xl">
            <ul className="space-y-3 mb-6">
              <li className="text-lg text-gray-600 leading-relaxed">Pre-program medical assessment required</li>
              <li className="text-lg text-gray-600 leading-relaxed">Funding may be available through approved third-party insurance schemes</li>
              <li className="text-lg text-gray-600 leading-relaxed">Individualised programs tailored to clinical needs</li>
            </ul>
            <div className="flex flex-col sm:flex-row gap-8 mb-8">
              <div>
                <span className="text-sm text-gray-500 block mb-1">Website</span>
                <a
                  href="https://www.acthealth.net.au"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="text-teal-700 font-semibold hover:underline cursor-pointer"
                >
                  www.acthealth.net.au
                </a>
              </div>
              <div>
                <span className="text-sm text-gray-500 block mb-1">Email</span>
                <a
                  href="mailto:clinic@acthealth.net.au"
                  className="text-teal-700 font-semibold hover:underline cursor-pointer"
                >
                  clinic@acthealth.net.au
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Location */}
        <section className="mb-24">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Location</h2>
          <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mb-4">
            Delivered through the{' '}
            <strong className="text-gray-900">Australasian Centre for Trauma (ACT)</strong>{' '}
            — a state-of-the-art multidisciplinary trauma recovery facility
          </p>
          <p className="text-lg text-gray-700 leading-relaxed max-w-3xl">
            154 Ipswich Road, Woolloongabba QLD 4102
          </p>
        </section>

        {/* Program Outline */}
        <section className="mb-24">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Program Outline</h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-6 max-w-3xl">
            The Intensive Multidisciplinary Treatment Program (IMTP) structure balances:
          </p>
          <ul className="space-y-3 max-w-3xl mb-6">
            <li className="text-lg text-gray-600 leading-relaxed">High-intensity clinical therapy</li>
            <li className="text-lg text-gray-600 leading-relaxed">Physical rehabilitation</li>
            <li className="text-lg text-gray-600 leading-relaxed">Psychoeducation</li>
            <li className="text-lg text-gray-600 leading-relaxed">Neuroplasticity-based interventions</li>
            <li className="text-lg text-gray-600 leading-relaxed">Restorative and experiential modalities</li>
          </ul>
          <p className="text-lg text-gray-600 leading-relaxed max-w-3xl">
            Programs vary according to clinical presentation and day vs residential
            participation.
          </p>
        </section>

        {/* Program Guide */}
        <section className="mb-24">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Program Guide</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300 text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">WEEK 1</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">MONDAY</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">TUESDAY</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">WEDNESDAY</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">THURSDAY</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">FRIDAY</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">SATURDAY</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">SUNDAY</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-white">
                  <td className="border border-gray-300 px-4 py-3 font-medium text-gray-900">8.00am</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Trauma Informed Strength &amp; Conditioning</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Trauma Informed Strength &amp; Conditioning</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Trauma Informed Strength &amp; Conditioning</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Trauma Informed Strength &amp; Conditioning</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Trauma Informed Strength &amp; Conditioning</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Trauma Informed Strength &amp; Conditioning</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Trauma Informed Strength &amp; Conditioning</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 font-medium text-gray-900">8.30am</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Group Check in and Peer Support</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Group Check in and Peer Support</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Group Check in and Peer Support</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Group Check in and Peer Support</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Group Check in and Peer Support</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Group Check in and Peer Support</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Group Check in and Peer Support</td>
                </tr>
                <tr className="bg-white">
                  <td className="border border-gray-300 px-4 py-3 font-medium text-gray-900">9.00am</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Core Therapy Session</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Core Therapy Session</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Core Therapy Session</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Core Therapy Session</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Core Therapy Session</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Core Therapy Session</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Core Therapy Session</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 font-medium text-gray-900">10.10am</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                </tr>
                <tr className="bg-white">
                  <td className="border border-gray-300 px-4 py-3 font-medium text-gray-900">10.45am</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Specialty Clinical Session – Individualised to patients</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Specialty Clinical Session – Individualised to patients</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Specialty Clinical Session – Individualised to patients</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Specialty Clinical Session – Individualised to patients</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Specialty Clinical Session – Individualised to patients</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Specialty Clinical Session – Individualised to patients</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Specialty Clinical Session – Individualised to Veteran</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 font-medium text-gray-900">12.00pm</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Lunch &amp; Social Engagement</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Lunch &amp; Social Engagement</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Lunch &amp; Social Engagement</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Lunch &amp; Social Engagement</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Lunch &amp; Social Engagement</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Lunch &amp; Social Engagement</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Lunch &amp; Social Engagement</td>
                </tr>
                <tr className="bg-white">
                  <td className="border border-gray-300 px-4 py-3 font-medium text-gray-900">1.00pm</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Physical Rehabilitation Session</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Physical Rehabilitation Session</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Physical Rehabilitation Session</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Physical Rehabilitation Session</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Physical Rehabilitation Session</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Physical Rehabilitation Session</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Physical Rehabilitation Session</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 font-medium text-gray-900">2.30pm</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                </tr>
                <tr className="bg-white">
                  <td className="border border-gray-300 px-4 py-3 font-medium text-gray-900">3.00pm</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Complimentary Therapies</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Complimentary Therapies</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Complimentary Therapies</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Complimentary Therapies</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Complimentary Therapies</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Complimentary Therapies</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Complimentary Therapies</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 font-medium text-gray-900">4.00pm</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                </tr>
                <tr className="bg-white">
                  <td className="border border-gray-300 px-4 py-3 font-medium text-gray-900">4.15pm</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Group Psychoeducation</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Group Psychoeducation</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Group Psychoeducation</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Group Psychoeducation</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Group Psychoeducation</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Group Psychoeducation</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Group Psychoeducation</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 font-medium text-gray-900">5.15pm</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Optional Fitness / Recreational Activity</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Optional Fitness / Recreational Activity</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Optional Fitness / Recreational Activity</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Optional Fitness / Recreational Activity</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Optional Fitness / Recreational Activity</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Optional Fitness / Recreational Activity</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Optional Fitness / Recreational Activity</td>
                </tr>
                <tr className="bg-white">
                  <td className="border border-gray-300 px-4 py-3 font-medium text-gray-900">6.00pm</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">BREAK</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 font-medium text-gray-900">6.30pm</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Dinner &amp; Social Engagement</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Dinner &amp; Social Engagement</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Dinner &amp; Social Engagement</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Dinner &amp; Social Engagement</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Dinner &amp; Social Engagement</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Dinner &amp; Social Engagement</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Dinner &amp; Social Engagement</td>
                </tr>
                <tr className="bg-white">
                  <td className="border border-gray-300 px-4 py-3 font-medium text-gray-900">8.00pm</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Reflection &amp; Reset</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Reflection &amp; Reset</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Reflection &amp; Reset</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Reflection &amp; Reset</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Reflection &amp; Reset</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Reflection &amp; Reset</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Reflection &amp; Reset</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Terms & Conditions */}
        <section className="mb-24">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Terms &amp; Conditions</h2>
          <ul className="space-y-3 max-w-3xl">
            <li className="text-lg text-gray-600 leading-relaxed">Prior medical clearance required</li>
            <li className="text-lg text-gray-600 leading-relaxed">Day and residential options available</li>
            <li className="text-lg text-gray-600 leading-relaxed">Meals provided</li>
            <li className="text-lg text-gray-600 leading-relaxed">Drug and alcohol-free environment</li>
            <li className="text-lg text-gray-600 leading-relaxed">Confidentiality and peer respect required</li>
            <li className="text-lg text-gray-600 leading-relaxed">Emergency procedures and 24/7 support</li>
            <li className="text-lg text-gray-600 leading-relaxed">Comprehensive discharge and follow-up plan</li>
          </ul>
        </section>

        {/* Take the Next Step */}
        <section className="mb-16 max-w-3xl">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Take the Next Step</h2>
          <p className="text-xl text-gray-700 leading-relaxed mb-2">
            Trauma changes the brain.
          </p>
          <p className="text-xl text-gray-700 leading-relaxed mb-8">
            Neuroplasticity allows it to change again.
          </p>
          <p className="text-lg text-gray-600 leading-relaxed mb-8">
            Visit{' '}
            <a
              href="https://www.acthealth.net.au"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="text-teal-700 font-semibold hover:underline cursor-pointer"
            >
              www.acthealth.net.au
            </a>{' '}
            to begin your assessment.
          </p>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => setIsReferralModalOpen(true)}
              className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white px-7 py-3.5 rounded-lg text-base font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              Online Referral
              <i className="ri-arrow-right-line"></i>
            </button>
            <a
              href="https://www.acthealth.net.au"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2 border border-gray-300 hover:border-gray-400 text-gray-700 px-7 py-3.5 rounded-lg text-base font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              Visit www.acthealth.net.au
              <i className="ri-external-link-line"></i>
            </a>
          </div>
        </section>

      </div>

      <Footer />

      <OnlineReferralModal
        isOpen={isReferralModalOpen}
        onClose={() => setIsReferralModalOpen(false)}
      />
    </div>
  );
}
